import { z } from 'zod'

export const CRM_ADMIN_READ_CONTRACT_VERSION = '1.0.0' as const

const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

/** Query keys accepted by every CRM admin read alias. Domain validation stays in
 * the shell handlers so existing org/page-size precedence is unchanged. */
export const crmAdminReadQuerySchema = z.object({
  orgId: optionalQueryString,
  org_id: optionalQueryString,
  page_size: optionalQueryString,
  pageSize: optionalQueryString,
}).passthrough()

const adminReadEvidenceSchema = z.object({
  db1TablesRead: z.array(z.enum(['user_profiles', 'org_user', 'domain_audit_log', 'app_error_events'])),
  rawSecretsReturned: z.literal(false),
  rawErrorStacksReturned: z.literal(false),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

const adminReadSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  db1Written: z.literal(false),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

const adminMetadataEvidenceSchema = z.object({
  db1TableRead: z.literal('crm_ingest_sources'),
  db1Written: z.boolean(),
  auditWritten: z.boolean(),
  rawSecretRead: z.literal(false),
  rawSecretWritten: z.literal(false),
  credentialPointerReturned: z.literal(false),
  providerCalled: z.literal(false),
  xanoTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

const adminIntegrationSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  db1Written: z.literal(false),
  auditWritten: z.literal(false),
  rawSecretRead: z.literal(false),
  rawSecretWritten: z.literal(false),
  providerCalled: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const crmAdminUserSummarySchema = z.object({
  id: z.string().min(1),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  status: z.string(),
  membershipStatus: z.string(),
  role: z.string(),
  provisionedVia: z.string(),
  acceptedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const crmAuditLogSummarySchema = z.object({
  id: z.string().min(1),
  service: z.string(),
  entityType: z.string(),
  entityId: z.string().nullable(),
  action: z.string(),
  actor: z.string(),
  actorId: z.string().nullable(),
  orgId: z.string().nullable(),
  changedFields: z.unknown(),
  metadata: z.unknown(),
  region: z.string(),
  createdAt: z.string(),
}).passthrough()

export const crmErrorEventSummarySchema = z.object({
  id: z.string().min(1),
  errorRef: z.string(),
  service: z.string(),
  errorPrefix: z.string(),
  errorContext: z.string(),
  message: z.string().nullable(),
  errorName: z.string().nullable(),
  requestMethod: z.string().nullable(),
  requestPath: z.string().nullable(),
  userEmail: z.string().nullable(),
  metadata: z.unknown(),
  createdAt: z.string(),
}).passthrough()

export const crmIntegrationSummarySchema = z.object({
  sourceId: z.string(),
  enabled: z.boolean(),
  secretRequired: z.boolean(),
  sharedSecretLast4: z.string().nullable(),
  hasSharedSecret: z.boolean(),
  rateLimitPerMinute: z.number().finite(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const crmAdminUsersResponseSchema = z.object({
  contract_version: z.literal(CRM_ADMIN_READ_CONTRACT_VERSION),
  users: z.array(crmAdminUserSummarySchema),
  readEvidence: adminReadEvidenceSchema,
  sideEffects: adminReadSideEffectsSchema,
}).passthrough()

export const crmAdminAuditLogsResponseSchema = z.object({
  contract_version: z.literal(CRM_ADMIN_READ_CONTRACT_VERSION),
  auditLogs: z.array(crmAuditLogSummarySchema),
  readEvidence: adminReadEvidenceSchema,
  sideEffects: adminReadSideEffectsSchema,
}).passthrough()

export const crmAdminErrorEventsResponseSchema = z.object({
  contract_version: z.literal(CRM_ADMIN_READ_CONTRACT_VERSION),
  errorEvents: z.array(crmErrorEventSummarySchema),
  readEvidence: adminReadEvidenceSchema,
  sideEffects: adminReadSideEffectsSchema,
}).passthrough()

export const crmAdminIntegrationsResponseSchema = z.object({
  contract_version: z.literal(CRM_ADMIN_READ_CONTRACT_VERSION),
  integrations: z.array(crmIntegrationSummarySchema),
  metadataEvidence: adminMetadataEvidenceSchema,
  sideEffects: adminIntegrationSideEffectsSchema,
}).passthrough()

export const CRM_ADMIN_READ_ERROR_CODES = [
  'CR_VALIDATION_FAILED',
  'CR_PERMISSION_DENIED',
  'CR_AUTH_UNAVAILABLE',
  'CR_MEMBERSHIP_UNAVAILABLE',
  'CR_DOMAIN_UNAVAILABLE',
] as const

export const crmAdminReadErrorEnvelopeSchema = z.object({
  contract_version: z.string().min(1),
  error: z.object({
    code: z.enum(CRM_ADMIN_READ_ERROR_CODES),
    message: z.string().min(1),
    reference: z.string().min(1),
    details: z.record(z.unknown()).optional(),
    request_id: z.string().optional(),
  }).passthrough(),
}).passthrough()

export type CrmAdminReadQuery = z.infer<typeof crmAdminReadQuerySchema>
export type CrmAdminUserSummary = z.infer<typeof crmAdminUserSummarySchema>
export type CrmAuditLogSummary = z.infer<typeof crmAuditLogSummarySchema>
export type CrmErrorEventSummary = z.infer<typeof crmErrorEventSummarySchema>
export type CrmIntegrationSummary = z.infer<typeof crmIntegrationSummarySchema>
export type CrmAdminUsersResponse = z.infer<typeof crmAdminUsersResponseSchema>
export type CrmAuditLogsResponse = z.infer<typeof crmAdminAuditLogsResponseSchema>
export type CrmErrorEventsResponse = z.infer<typeof crmAdminErrorEventsResponseSchema>
export type CrmAdminIntegrationsResponse = z.infer<typeof crmAdminIntegrationsResponseSchema>
export type CrmAdminReadErrorCode = (typeof CRM_ADMIN_READ_ERROR_CODES)[number]
export type CrmAdminReadErrorEnvelope = z.infer<typeof crmAdminReadErrorEnvelopeSchema>
